import React from 'react';
import ReactDOM from 'react-dom';
import AkMoInvoicingFormat from '../components/templates/AkMoInvoicingFormat';
import ThermalInvoiceFormatRegular from '../components/templates/ThermalInvoiceFormatRegular';
import ThermalInvoiceFormatUnregistered from '../components/templates/ThermalInvoiceFormatUnregistered';
import ThermalInvoiceFormatComposition from '../components/templates/ThermalInvoiceFormatComposition';
// Import original format for backward compatibility 
import ThermalInvoiceFormat from '../components/templates/ThermalInvoiceFormat';
import { InvoiceTemplateType, invoiceTemplateOptions } from './invoiceTemplates';

/**
 * Utility for printing invoices in different formats
 */
export class InvoicePrinter {
  /**
   * Print an invoice in standard format
   * @param businessDetails Business details for the invoice
   * @param clientDetails Client details for the invoice
   * @param invoiceDetails Invoice details
   * @param items Line items for the invoice
   * @param templateId Optional template ID to use (defaults to AkMo Invoice - 1)
   * @returns Promise that resolves when printing is initiated
   */
  public static async printInvoice(
    businessDetails: any,
    clientDetails: any,
    invoiceDetails: any,
    items: any[],
    templateId?: number | InvoiceTemplateType
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        // Always use AkMo Invoice template (ID: 1)
        const fixedTemplateId = 1;
        
        // Find the AkMo template
        const akmoTemplate = invoiceTemplateOptions.find(t => t.id === fixedTemplateId);
        
        // Get the template colors
        let primaryColor = '#4776E6'; // Default primary color
        let secondaryColor = '#8E54E9'; // Default secondary color
        
        if (akmoTemplate && akmoTemplate.colorScheme) {
          primaryColor = akmoTemplate.colorScheme.primary;
          secondaryColor = akmoTemplate.colorScheme.secondary;
        }
        
        // Clone the existing preview element if present in the DOM
        const existingPreview = document.getElementById('invoice-preview');
        if (existingPreview) {
          // Create a print container
          const printContainer = document.createElement('div');
          printContainer.style.position = 'fixed';
          printContainer.style.left = '0';
          printContainer.style.top = '0';
          printContainer.style.width = '210mm'; // A4 width
          printContainer.style.height = 'auto';
          printContainer.style.backgroundColor = 'white';
          printContainer.style.zIndex = '9999';
          printContainer.id = 'print-invoice-container';
          
          // Render invoice to the container using the clone of the preview
          ReactDOM.render(
            React.createElement(AkMoInvoicingFormat, {
              businessDetails,
              clientDetails,
              invoiceDetails,
              items: items.map(item => ({
                productName: item.name || item.productName,
                description: item.description || '',
                quantity: item.quantity,
                unit: item.unit || 'Nos',
                hsn: item.hsnCode || item.hsn || '',
                gstRate: item.gstRate || 0,
                taxableValue: item.taxableValue || (item.quantity * item.rate),
                taxAmount: item.taxAmount || ((item.quantity * item.rate * (item.gstRate || 0)) / 100),
                total: item.amount || item.total,
              })),
              primaryColor,
              secondaryColor,
            }),
            printContainer,
            () => {
              document.body.appendChild(printContainer);
              
              // Once rendered, trigger print
              setTimeout(() => {
                const prevTitle = document.title;
                document.title = `Invoice ${invoiceDetails.invoiceNumber}`;
                
                // Store current body overflow
                const prevOverflow = document.body.style.overflow;
                document.body.style.overflow = 'visible';
                
                // Add print stylesheet
                const style = document.createElement('style');
                style.innerHTML = `
                  @media print {
                    body > *:not(#print-invoice-container) {
                      display: none !important;
                    }
                    #print-invoice-container {
                      position: absolute !important;
                      left: 0 !important;
                      top: 0 !important;
                      width: 210mm !important;
                      height: auto !important;
                      margin: 0 !important;
                      padding: 0 !important;
                      visibility: visible !important;
                      background-color: white !important;
                      z-index: 9999 !important;
                      transform: scale(1) !important;
                      transform-origin: top left !important;
                    }
                    #print-invoice-container * {
                      visibility: visible !important;
                      color-adjust: exact !important;
                      print-color-adjust: exact !important;
                      -webkit-print-color-adjust: exact !important;
                    }
                    /* Preserve all colors */
                    #print-invoice-container [style*="background"], 
                    #print-invoice-container [style*="color"],
                    #print-invoice-container [class*="color-"],
                    #print-invoice-container [class*="bg-"],
                    #print-invoice-container div,
                    #print-invoice-container span,
                    #print-invoice-container p,
                    #print-invoice-container h1,
                    #print-invoice-container h2,
                    #print-invoice-container h3,
                    #print-invoice-container h4,
                    #print-invoice-container h5,
                    #print-invoice-container h6,
                    #print-invoice-container table,
                    #print-invoice-container th,
                    #print-invoice-container td {
                      color-adjust: exact !important;
                      print-color-adjust: exact !important;
                      -webkit-print-color-adjust: exact !important;
                    }
                    @page {
                      size: A4 portrait;
                      margin: 0mm;
                    }
                  }
                `;
                
                document.head.appendChild(style);
                
                // Small delay to ensure stylesheet is applied
                setTimeout(() => {
                  window.print();
                  
                  // Cleanup
                  setTimeout(() => {
                    document.head.removeChild(style);
                    document.title = prevTitle;
                    document.body.style.overflow = prevOverflow;
                    // Check if the container is still in the DOM before trying to remove it
                    if (document.body.contains(printContainer)) {
                      document.body.removeChild(printContainer);
                    }
                    resolve();
                  }, 1000);
                }, 300);
              }, 500);
            }
          );
          
          return;
        }
        
        // If no existing preview found or if the preview doesn't contain the component,
        // create a new print container and render the component
        const printContainer = document.createElement('div');
        printContainer.style.position = 'fixed';
        printContainer.style.left = '0';
        printContainer.style.top = '0';
        printContainer.style.width = '210mm'; // A4 width
        printContainer.style.height = 'auto';
        printContainer.style.backgroundColor = 'white';
        printContainer.style.zIndex = '9999';
        printContainer.id = 'print-invoice-container';
        document.body.appendChild(printContainer);
        
        // Format data for AkMoInvoicingFormat
        const formattedItems = items.map(item => ({
          productName: item.name || item.productName,
          description: item.description || '',
          quantity: item.quantity,
          unit: item.unit || 'Nos',
          hsn: item.hsnCode || item.hsn || '',
          gstRate: item.gstRate || 0,
          taxableValue: item.taxableValue || (item.quantity * item.rate),
          taxAmount: item.taxAmount || ((item.quantity * item.rate * (item.gstRate || 0)) / 100),
          total: item.amount || item.total,
        }));
        
        // Render invoice to the container
        ReactDOM.render(
          React.createElement(AkMoInvoicingFormat, {
            businessDetails,
            clientDetails,
            invoiceDetails,
            items: formattedItems,
            primaryColor,
            secondaryColor,
          }),
          printContainer,
          () => {
            // Once rendered, trigger print
            setTimeout(() => {
              const prevTitle = document.title;
              document.title = `Invoice ${invoiceDetails.invoiceNumber}`;
              
              // Store current body overflow
              const prevOverflow = document.body.style.overflow;
              document.body.style.overflow = 'visible';
              
              // Add print stylesheet
              const style = document.createElement('style');
              style.innerHTML = `
                @media print {
                  body > *:not(#print-invoice-container) {
                    display: none !important;
                  }
                  #print-invoice-container {
                    position: absolute !important;
                    left: 0 !important;
                    top: 0 !important;
                    width: 210mm !important;
                    height: auto !important;
                    margin: 0 !important;
                    padding: 0 !important;
                    visibility: visible !important;
                    background-color: white !important;
                    z-index: 9999 !important;
                  }
                  #print-invoice-container * {
                    visibility: visible !important;
                    color-adjust: exact !important;
                    print-color-adjust: exact !important;
                    -webkit-print-color-adjust: exact !important;
                  }
                  /* Preserve all colors */
                  #print-invoice-container [style*="background"], 
                  #print-invoice-container [style*="color"],
                  #print-invoice-container [class*="color-"],
                  #print-invoice-container [class*="bg-"],
                  #print-invoice-container div,
                  #print-invoice-container span,
                  #print-invoice-container p,
                  #print-invoice-container h1,
                  #print-invoice-container h2,
                  #print-invoice-container h3,
                  #print-invoice-container h4,
                  #print-invoice-container h5,
                  #print-invoice-container h6,
                  #print-invoice-container table,
                  #print-invoice-container th,
                  #print-invoice-container td {
                    color-adjust: exact !important;
                    print-color-adjust: exact !important;
                    -webkit-print-color-adjust: exact !important;
                  }
                  @page {
                    size: A4 portrait;
                    margin: 0mm;
                  }
                }
              `;
              
              document.head.appendChild(style);
              
              // Small delay to ensure stylesheet is applied
              setTimeout(() => {
                window.print();
                
                // Cleanup
                setTimeout(() => {
                  document.head.removeChild(style);
                  document.title = prevTitle;
                  document.body.style.overflow = prevOverflow;
                  // Check if the container is still in the DOM before trying to remove it
                  if (document.body.contains(printContainer)) {
                    document.body.removeChild(printContainer);
                  }
                  resolve();
                }, 1000);
              }, 300);
            }, 500);
          }
        );
      } catch (error) {
        console.error('Error printing invoice:', error);
        reject(error);
      }
    });
  }
  
  /**
   * Print an invoice in thermal printer format
   * @param businessDetails Business details for the invoice
   * @param clientDetails Client details for the invoice
   * @param invoiceDetails Invoice details with items
   * @returns Promise that resolves when printing is initiated
   */
  public static async printThermalInvoice(
    businessDetails: any,
    clientDetails: any,
    invoiceDetails: any,
    widthPreset: '3-inch' | '2-inch' = '3-inch'
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const width = widthPreset === '2-inch' ? '58mm' : '80mm';

        // Create a temporary print container
        const printContainer = document.createElement('div');
        printContainer.style.position = 'absolute';
        printContainer.style.left = '-9999px';
        printContainer.style.top = '0';
        printContainer.style.width = width;
        document.body.appendChild(printContainer);
        
        // Format invoice items for thermal format
        const items = invoiceDetails.items.map((item: any) => ({
          description: item.name || item.description,
          quantity: item.quantity,
          rate: item.rate,
          gstRate: item.gstRate || 0,
          amount: item.amount,
        }));
        
        // Format invoice details for thermal format
        const thermalInvoiceDetails = {
          invoiceNumber: invoiceDetails.invoiceNumber,
          invoiceDate: invoiceDetails.invoiceDate,
          items,
          discount: invoiceDetails.discount,
          additionalCharges: Array.isArray(invoiceDetails.additionalCharges) ? invoiceDetails.additionalCharges : [],
          termsAndConditions: Array.isArray(invoiceDetails.termsAndConditions)
            ? invoiceDetails.termsAndConditions
            : typeof invoiceDetails.termsAndConditions === 'string'
              ? [invoiceDetails.termsAndConditions]
              : [],
          isInterstate: businessDetails.state !== clientDetails.state,
          placeOfSupply: invoiceDetails.placeOfSupply
        };
        
        // Determine which thermal invoice template to use based on GST registration type
        const gstRegistered = businessDetails.gstRegistered || 'not_registered';
        const gstType = businessDetails.gstType || 'regular';
        
        let ThermalComponent;
        if (gstRegistered === 'registered') {
          if (gstType === 'composition') {
            ThermalComponent = ThermalInvoiceFormatComposition;
          } else {
            // Regular GST registered user
            ThermalComponent = ThermalInvoiceFormatRegular;
          }
        } else {
          // Not registered under GST
          ThermalComponent = ThermalInvoiceFormatUnregistered;
        }
        
        // Render thermal invoice to the container
        const renderComponent = (copyLabel?: string, isCollectingCopy?: boolean) => React.createElement(ThermalComponent as React.ComponentType<any>, {
            businessDetails,
            clientDetails,
            invoiceDetails: { ...thermalInvoiceDetails, copyLabel, isCollectingCopy },
            widthPreset,
        });

        const printContent = invoiceDetails.printCollectingCopy ? (
          React.createElement(React.Fragment, null,
            renderComponent('ORIGINAL RECEIPT', false),
            React.createElement('div', { style: { pageBreakAfter: 'always', marginBottom: '20px' } }),
            renderComponent('COLLECTING COPY', true)
          )
        ) : renderComponent();

        ReactDOM.render(
          printContent,
          printContainer,
          () => {
            // Once rendered, trigger print in-window (exactly like A4 print, 100% reliable)
            let checkAttempts = 0;
            const executePrint = () => {
              if (printContainer.innerHTML.trim() === '' && checkAttempts < 20) {
                checkAttempts++;
                setTimeout(executePrint, 50);
                return;
              }

              try {
                // Set the ID for matching our print stylesheet
                printContainer.id = 'thermal-print-container';

                const prevTitle = document.title;
                document.title = `Receipt ${invoiceDetails.invoiceNumber}`;

                // Add print stylesheet
                const style = document.createElement('style');
                style.innerHTML = `
                  @media print {
                    body > *:not(#thermal-print-container) {
                      display: none !important;
                    }
                    #thermal-print-container {
                      position: absolute !important;
                      left: 0 !important;
                      top: 0 !important;
                      width: ${width} !important;
                      height: auto !important;
                      margin: 0 !important;
                      padding: 0 !important;
                      visibility: visible !important;
                      background-color: white !important;
                      z-index: 9999 !important;
                    }
                    #thermal-print-container * {
                      visibility: visible !important;
                      color-adjust: exact !important;
                      print-color-adjust: exact !important;
                      -webkit-print-color-adjust: exact !important;
                    }
                    @page {
                      size: ${width} auto;
                      margin: 0mm;
                    }
                  }
                `;
                document.head.appendChild(style);

                // Small delay to ensure stylesheet is applied before print
                setTimeout(() => {
                  window.print();

                  // Cleanup after printing dialog is closed
                  setTimeout(() => {
                    if (document.head.contains(style)) {
                      document.head.removeChild(style);
                    }
                    document.title = prevTitle;
                    if (document.body.contains(printContainer)) {
                      document.body.removeChild(printContainer);
                    }
                    resolve();
                  }, 1000);
                }, 300);

              } catch (err) {
                console.error('Error during thermal print execution:', err);
                if (document.body.contains(printContainer)) {
                  document.body.removeChild(printContainer);
                }
                reject(err);
              }
            };
            executePrint();
          }
        );
      } catch (error) {
        console.error('Error printing thermal invoice:', error);
        reject(error);
      }
    });
  }
}

export default InvoicePrinter; 